import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form, Input, Select, Row, Col, Switch, Tabs, message,
  Button, Space, Breadcrumb, Card, Divider, Tooltip, Tag, Typography, ConfigProvider, theme
} from 'antd';
import {
  SaveOutlined, CloseOutlined, EyeOutlined, HistoryOutlined,
  CheckCircleFilled, HomeOutlined, FileTextOutlined,
  InfoCircleOutlined, ThunderboltFilled, RocketFilled,
  AppstoreFilled, SettingFilled, LeftOutlined, CodeFilled
} from '@ant-design/icons';
import styled from 'styled-components';
import SimplePromptEditor from './SimplePromptEditor';
import VisualPromptBuilder from './VisualPromptBuilder';
import EnhancedHTMLEditor from './EnhancedHTMLEditor';
import CodeEditor from 'components/shared/CodeEditor';
import templateService from 'services/templateService';
import execute from 'hooks/execute';

const { TextArea } = Input;
const { Title, Text } = Typography;
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

/**
 * FullPageEditor - Pro Console Template Orchestrator
 * Revamped for "Aura" design system
 */
const FullPageEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [template, setTemplate] = useState(null);

  // Editor states
  const [promptDef, setPromptDef] = useState(null);
  const [htmlTemplate, setHtmlTemplate] = useState('');
  const [dataMapping, setDataMapping] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [promptEditorMode, setPromptEditorMode] = useState('simple'); // 'simple' or 'visual'

  // Assignment states
  const [modules, setModules] = useState([]);
  const [pages, setPages] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [scope, setScope] = useState('SYSTEM');

  // Auto-save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [modRes, orgRes] = await Promise.all([
          execute('GET', `${BASE_URL}/modules`),
          execute('GET', `${BASE_URL}/organizations`)
        ]);
        setModules((modRes.data || modRes || []).filter(m => m?.Id));
        setOrganizations((orgRes.data || orgRes || []).filter(o => o?.Org_ID));
      } catch (err) {
        console.error('Meta fetch error:', err);
      }
    };
    fetchMeta();
  }, []);
  useEffect(() => {
    const refreshPages = async () => {
      if (selectedModules.length === 0) return;
      
      try {
        setLoading(true);
        // Build query string for multiple modules
        const queryParams = selectedModules.map(m => `moduleIds=${m}`).join('&');
        const pageRes = await execute('GET', `${BASE_URL}/v1/templates/pages/discovery?${queryParams}`);
        
        if (pageRes?.data && Array.isArray(pageRes.data)) {
          setPages(pageRes.data);
        }
      } catch (err) {
        console.warn('Page discovery for modules failed:', err);
      } finally {
        setLoading(false);
      }
    };

    refreshPages();
  }, [selectedModules]);

  // Load template if editing
  useEffect(() => {
    if (id) loadTemplate(id);
    else {
      // New template - set defaults
      setEditMode(false);
      setHtmlTemplate(getDefaultHtmlTemplate());
      setDataMapping('{}');
    }
  }, [id]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadTemplate = async (templateId) => {
    try {
      setLoading(true);
      const response = await templateService.getTemplateById(templateId);

      if (response.success) {
        const tmpl = response.data;
        setTemplate(tmpl);
        setEditMode(true);
        setScope(tmpl.scope || 'SYSTEM');

        form.setFieldsValue({
          templateName: tmpl.templateName || tmpl.template_name,
          analysisLevel: tmpl.analysisLevel || tmpl.analysis_level,
          description: tmpl.description,
          category: tmpl.category,
          scope: tmpl.scope,
          isActive: tmpl.isActive ?? tmpl.is_active ?? true,
          isDefault: tmpl.isDefault ?? tmpl.is_default ?? false,
        });

        setPromptDef(tmpl.promptDefinition || tmpl.prompt_definition);
        setHtmlTemplate(tmpl.htmlTemplate || tmpl.html_template || getDefaultHtmlTemplate());
        setDataMapping(JSON.stringify(tmpl.dataMapping || tmpl.data_mapping || {}, null, 2));

        // Fetch assignments
        const [mRes, pRes, oRes] = await Promise.all([
          execute('GET', `${BASE_URL}/v1/templates/${templateId}/modules`),
          execute('GET', `${BASE_URL}/v1/templates/${templateId}/pages`),
          execute('GET', `${BASE_URL}/v1/templates/${templateId}/organizations`)
        ]);
        setSelectedModules((mRes.data || mRes || []).map(m => m.moduleId));
        setSelectedPages((pRes.data || pRes || []).map(p => p.pageId));
        setSelectedOrganizations((oRes.data || oRes || []).map(o => o.organizationId));
      }
    } catch (error) {
      message.error(error.message);
      navigate('/template-management');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let parsedMapping = {};
      try { parsedMapping = JSON.parse(dataMapping); } catch {
        message.error('Invalid JSON in Data Mapping');
        setActiveTab('mapping'); return;
      }

      const payload = {
        ...values,
        promptDefinition: promptDef,
        htmlTemplate,
        dataMapping: parsedMapping,
        isDraft: false
      };

      setSaving(true);
      let tId = id;
      if (editMode && id) {
        await templateService.updateTemplate(id, payload);
        message.success('Intelligence Protocol Updated');
      } else {
        const res = await templateService.createTemplate(payload);
        tId = res.data?.templateId;
        message.success('Intelligence Protocol Established');
      }

      // Sync Assignments
      await Promise.all([
        execute('POST', `${BASE_URL}/v1/templates/${tId}/modules`, { moduleIds: selectedModules }),
        execute('POST', `${BASE_URL}/v1/templates/${tId}/pages`, { pageIds: selectedPages }),
        scope === 'ORGANIZATION' ? execute('POST', `${BASE_URL}/v1/templates/${tId}/organizations`, { organizationIds: selectedOrganizations }) : Promise.resolve()
      ]);

      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      if (!editMode) navigate(`/template-management/edit/${tId}`);
    } catch (error) {
       message.error(error.message || 'Validation failed');
    } finally {
      setSaving(false);
    }
  };

  const getDefaultHtmlTemplate = () => `<!DOCTYPE html><html><body><h1>{{reportTitle}}</h1></body></html>`;

  return (
    <ConfigProvider 
      theme={{ 
        algorithm: theme.defaultAlgorithm, 
        token: { 
          colorPrimary: '#0a192f', 
          borderRadius: 8,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        } 
      }}
    >
      <EditorWrapper>
        <AuraHeader>
          <HeaderContent>
            <HeaderLeft>
              <ActionIcon onClick={() => navigate('/template-management')}><LeftOutlined /></ActionIcon>
              <Divider type="vertical" style={{ height: 20, margin: '0 20px', borderColor: '#f1f5f9' }} />
              <TitleGroup>
                <Title level={5} style={{ margin: 0, color: 'var(--aura-navy)', fontWeight: 750, fontSize: 14, letterSpacing: '-0.3px' }}>
                  {editMode ? template?.templateName : 'New Template'}
                </Title>
              </TitleGroup>
            </HeaderLeft>

            <HeaderRight>
              <Space size="middle">
                {hasUnsavedChanges && <StatusBadge>Unsaved Changes</StatusBadge>}
                <MinimalBtn icon={<EyeOutlined />} onClick={() => navigate(`/template-management/preview/${id}`)}>Preview</MinimalBtn>
                <MinimalBtn icon={<HistoryOutlined />} onClick={() => navigate(`/template-management/history/${id}`)}>History</MinimalBtn>
                <PrimaryAction
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saving}
                  onClick={handleSubmit}
                >
                  Save Template
                </PrimaryAction>
              </Space>
            </HeaderRight>
          </HeaderContent>
        </AuraHeader>

        <ContentGrid>
          <StyledTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'basic',
                label: <><SettingFilled /> Configuration</>,
                children: (
                  <TabInner>
                    <Row gutter={24}>
                      <Col span={16}>
                        <AuraCard title="Core Parameters">
                          <Form form={form} layout="vertical" onValuesChange={() => setHasUnsavedChanges(true)}>
                            <Row gutter={16}>
                              <Col span={14}>
                                <Form.Item label="Template Name" name="templateName" rules={[{required: true}]}>
                                  <PremiumInput placeholder="e.g., Strategic Revenue Synthesis" />
                                </Form.Item>
                              </Col>
                              <Col span={10}>
                                <Form.Item label="Analysis Level" name="analysisLevel" rules={[{required: true}]}>
                                  <PremiumSelect>
                                    <Select.Option value="page">Page Level</Select.Option>
                                    <Select.Option value="module">Module Level</Select.Option>
                                    <Select.Option value="dashboard">Dashboard Level</Select.Option>
                                    <Select.Option value="hrst">Global Level</Select.Option>
                                  </PremiumSelect>
                                </Form.Item>
                              </Col>
                            </Row>
                            <Form.Item label="Description" name="description">
                              <PremiumTextArea rows={4} maxLength={500} showCount />
                            </Form.Item>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item label="Category" name="category">
                                  <PremiumInput placeholder="e.g., Financial Reports" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item label="Scope" name="scope" rules={[{required: true}]}>
                                  <PremiumSelect onChange={setScope}>
                                    <Select.Option value="SYSTEM">Global (All Orgs)</Select.Option>
                                    <Select.Option value="ORGANIZATION">Organization Specific</Select.Option>
                                    <Select.Option value="USER">User Specific</Select.Option>
                                  </PremiumSelect>
                                </Form.Item>
                              </Col>
                            </Row>
                            <Divider />
                            <Row>
                              <Col span={12}>
                                <Form.Item label="Status" name="isActive" valuePropName="checked">
                                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item label="Default Template" name="isDefault" valuePropName="checked">
                                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Form>
                        </AuraCard>
                      </Col>
                      <Col span={8}>
                        <AuraCard title="Assignments">
                          <MappingSection>
                            <MappingTitle><AppstoreFilled /> Assigned Modules</MappingTitle>
                            <PremiumSelect
                              mode="multiple"
                              placeholder="Select Modules"
                              value={selectedModules}
                              onChange={setSelectedModules}
                              style={{ width: '100%', marginBottom: 24 }}
                            >
                              {modules.map(m => <Select.Option key={m.Id} value={m.Id}>{m.ModuleName}</Select.Option>)}
                            </PremiumSelect>

                            <MappingTitle style={{ color: '#531dab' }}><FileTextOutlined /> Assigned Pages</MappingTitle>
                            <PremiumSelect
                              mode="multiple"
                              placeholder="Select Clinical Pages"
                              value={selectedPages}
                              onChange={setSelectedPages}
                              style={{ width: '100%' }}
                              dropdownStyle={{ borderRadius: 8 }}
                            >
                              {pages.map(p => <Select.Option key={p.pageId} value={p.pageId}>{p.pageName}</Select.Option>)}
                            </PremiumSelect>

                            {scope === 'ORGANIZATION' && (
                              <div style={{ marginTop: 24 }}>
                                <MappingTitle><ThunderboltFilled /> Assigned Organizations</MappingTitle>
                                <PremiumSelect
                                  mode="multiple"
                                  placeholder="Select Organizations"
                                  value={selectedOrganizations}
                                  onChange={setSelectedOrganizations}
                                  style={{ width: '100%' }}
                                >
                                  {organizations.map(o => <Select.Option key={o.Org_ID} value={o.Org_ID}>{o.Org_Name}</Select.Option>)}
                                </PremiumSelect>
                              </div>
                            )}
                          </MappingSection>
                        </AuraCard>

                        <AuraCard title="System Status" style={{ marginTop: 24 }}>
                           <StatusLine>
                            <div className="dot active"></div>
                            <Text strong>Ready</Text>
                           </StatusLine>
                           <Text type="secondary" style={{ fontSize: 11 }}>The engine is ready for deployment.</Text>
                        </AuraCard>
                      </Col>
                    </Row>
                  </TabInner>
                )
              },
              {
                key: 'prompts',
                label: <><RocketFilled /> AI Prompts</>,
                children: (
                  <TabInner>
                    <OrchHeader>
                      <OrchSwitcher>
                        <SwitchBtn $active={promptEditorMode === 'simple'} onClick={() => setPromptEditorMode('simple')}>Simple Editor</SwitchBtn>
                        <SwitchBtn $active={promptEditorMode === 'visual'} onClick={() => setPromptEditorMode('visual')}>Visual Builder</SwitchBtn>
                      </OrchSwitcher>
                    </OrchHeader>
                    {promptEditorMode === 'simple' ? (
                      <SimplePromptEditor value={promptDef} onChange={val => { setPromptDef(val); setHasUnsavedChanges(true); }} />
                    ) : (
                      <VisualPromptBuilder value={promptDef} onChange={val => { setPromptDef(val); setHasUnsavedChanges(true); }} />
                    )}
                  </TabInner>
                )
              },
              {
                key: 'html',
                label: <><FileTextOutlined /> HTML Template</>,
                children: (
                  <TabInner>
                    <HelpBar>
                      <InfoCircleOutlined />
                      <Text style={{ fontSize: 12, marginLeft: 8 }}>Provide a reference HTML structure for the AI to base its report on.</Text>
                    </HelpBar>
                    <EnhancedHTMLEditor value={htmlTemplate} onChange={val => { setHtmlTemplate(val); setHasUnsavedChanges(true); }} />
                  </TabInner>
                )
              },
              {
                key: 'mapping',
                label: <><CodeFilled /> Data Mapping</>,
                children: (
                  <TabInner>
                    <CodeEditor
                       language="json"
                       value={dataMapping}
                       onChange={val => { setDataMapping(val); setHasUnsavedChanges(true); }}
                       height="600px"
                    />
                  </TabInner>
                )
              }
            ]}
          />
        </ContentGrid>
      </EditorWrapper>
    </ConfigProvider>
  );
};

const EditorWrapper = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'Inter', sans-serif;
`;

const AuraHeader = styled.div`
  background: white;
  height: 64px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: 1512px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ActionIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--aura-slate);
  &:hover { background: #f1f5f9; color: var(--aura-navy); }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProBadge = styled.span`
  font-size: 8px;
  font-weight: 950;
  color: #64ffda;
  background: var(--aura-navy);
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.8px;
  margin-bottom: 2px;
  display: inline-block;
  width: fit-content;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const StatusBadge = styled.div`
  font-size: 9px;
  font-weight: 850;
  color: #f59e0b;
  background: #fffbeb;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.5px;
  border: 1px solid #fef3c7;
`;

const MinimalBtn = styled(Button)`
  font-weight: 700 !important;
  font-size: 11px !important;
  letter-spacing: 0.3px !important;
  height: 36px !important;
  border-radius: 6px !important;
  border-color: #f1f5f9 !important;
  color: var(--aura-slate) !important;
  box-shadow: none !important;
  &:hover { background: #f8fafc !important; color: var(--aura-navy) !important; border-color: #e2e8f0 !important; }
`;

const PrimaryAction = styled(Button)`
  font-weight: 800 !important;
  font-size: 11px !important;
  height: 36px !important;
  padding: 0 20px !important;
  border-radius: 6px !important;
  background: var(--aura-blue) !important;
  border: none !important;
  box-shadow: 0 2px 4px rgba(10, 25, 47, 0.1) !important;
  &:hover { background: #0284c7 !important; transform: translateY(-1px); }
`;

const ContentGrid = styled.div`
  max-width: 1512px;
  margin: 0 auto;
  padding: 24px;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px !important;
    &::before { border-bottom: 1px solid #f1f5f9; }
  }
  .ant-tabs-tab {
    padding: 10px 0 !important;
    margin-right: 32px !important;
    &-btn {
      font-weight: 800 !important;
      font-size: 12px !important;
      color: var(--aura-slate) !important;
      display: flex;
      align-items: center;
      gap: 8px;
      letter-spacing: 0.3px;
    }
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn { color: var(--aura-navy) !important; }
  .ant-tabs-ink-bar { background: var(--aura-navy) !important; height: 2px !important; }
`;

const TabInner = styled.div`
  animation: fadeIn 0.3s ease-out;
  @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
`;

const AuraCard = styled(Card)`
  border-radius: 8px !important;
  border: 1px solid #f1f5f9 !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.01) !important;
  margin-bottom: 20px;
  
  .ant-card-head {
    min-height: 48px !important;
    border-bottom: 1px solid #f1f5f9 !important;
    padding: 0 16px !important;
  }
  .ant-card-head-title {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: var(--aura-navy) !important;
  }
  .ant-card-body { padding: 20px !important; }
`;

const PremiumInput = styled(Input)`
  height: 36px !important;
  border-radius: 6px !important;
  background: white !important;
  border-color: #f1f5f9 !important;
  font-size: 13px !important;
  &:hover, &:focus { border-color: var(--aura-navy) !important; }
`;

const PremiumTextArea = styled(TextArea)`
  border-radius: 6px !important;
  background: white !important;
  border-color: #f1f5f9 !important;
  padding: 10px 12px !important;
  font-size: 13px !important;
`;

const PremiumSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    height: 36px !important;
    border-radius: 6px !important;
    background: white !important;
    border-color: #f1f5f9 !important;
    display: flex !important;
    align-items: center !important;
    font-size: 13px !important;
  }
`;

const MappingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MappingTitle = styled.div`
  font-size: 10px;
  font-weight: 900;
  color: var(--aura-slate);
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const HelpBar = styled.div`
  background: var(--aura-navy);
  padding: 10px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
`;

const OrchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 4px;
`;

const OrchSwitcher = styled.div`
  display: flex;
  background: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
`;

const SwitchBtn = styled.button`
  height: 28px;
  padding: 0 16px;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? 'var(--aura-navy)' : 'var(--aura-slate)'};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'};
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #cbd5e1;
    
    &.active {
      background: #10b981;
      box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
    }
  }
`;

export default FullPageEditor;

